/**
 * Whose campaign is this, and whose LinkedIn does a bulk action send from? The pure half of the
 * campaign-owner guard: the page banner, the bulk confirms and the account switch all word things
 * through these, so the copy can't drift between them.
 */

/** A teammate as the guard names them: the campaign owner, the person you're acting as, a sender. */
export type CampaignPerson = {
  userId: string;
  name: string | null;
  email?: string | null;
  profilePicture: string | null;
};

/** How many of a bulk action's people send from one person's LinkedIn. */
export type SenderCount = { person: CampaignPerson; count: number };

/** Full display name: their name on the team, else their email, never blank. */
export const personName = (p: Pick<CampaignPerson, 'name' | 'email'>): string =>
  p.name?.trim() || p.email?.trim() || 'Someone';

/** First name only, for the second mention in a sentence ("Josh's LinkedIn"). */
export const firstName = (p: Pick<CampaignPerson, 'name' | 'email'>): string => {
  const full = personName(p);
  // An email has no first name; say it whole.
  return full.includes('@') ? full : (full.split(/\s+/)[0] ?? full);
};

/** "Josh Volgarino's campaign". */
export const campaignOf = (owner: Pick<CampaignPerson, 'name' | 'email'>): string => `${personName(owner)}'s campaign`;

/** Is the campaign someone else's than the person you're acting as? Unknown either side = no warning. */
export const isOwnerMismatch = (
  owner: Pick<CampaignPerson, 'userId'> | null | undefined,
  actingUserId: string | null | undefined,
): boolean => owner != null && actingUserId != null && owner.userId !== actingUserId;

/** Count rows per owner (`user_id`), most first — whose LinkedIn each would send from. */
export function senderCountsOf(rows: readonly { user_id?: string | null }[]): { userId: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (r.user_id == null) continue;
    counts.set(r.user_id, (counts.get(r.user_id) ?? 0) + 1);
  }
  return [...counts]
    .map(([userId, count]) => ({ userId, count }))
    .sort((a, b) => b.count - a.count || a.userId.localeCompare(b.userId));
}

/** Put names and pictures on sender counts; someone not in `people` keeps their id and no name. */
export function resolveSenders(
  counts: readonly { userId: string; count: number }[],
  people: readonly CampaignPerson[],
): SenderCount[] {
  const byId = new Map(people.map((p) => [p.userId, p]));
  return counts.map(({ userId, count }) => ({
    person: byId.get(userId) ?? { userId, name: null, profilePicture: null },
    count,
  }));
}

/**
 * "Josh's LinkedIn" — or, when the people send from several accounts, "Josh's LinkedIn (180) and
 * Steven's (26)". The counts only show when there is more than one sender.
 */
export function sendsFromLine(senders: readonly SenderCount[]): string {
  if (senders.length === 0) return '';
  if (senders.length === 1) return `${firstName(senders[0]!.person)}'s LinkedIn`;
  const parts = senders.map((s, i) =>
    i === 0
      ? `${firstName(s.person)}'s LinkedIn (${s.count.toLocaleString()})`
      : `${firstName(s.person)}'s (${s.count.toLocaleString()})`,
  );
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** What a tab last saw on a campaign page: whose campaign, acting as whom. */
export type CampaignPageSeen = { templateId: string; actingUserId: string; ownerUserId: string };

/**
 * The person this tab acts as changed while it showed a campaign — a switch here (⌘⇧K, the banner,
 * Stop) or in another tab (the storage listener follows it). Stay only when the campaign is the new
 * person's own; otherwise leave for the campaigns list, so nobody lands "in" a campaign that isn't
 * theirs. A fresh visit (nothing seen on this campaign yet) or the same person stays.
 */
export function campaignPageOnIdentity(
  lastSeen: CampaignPageSeen | null,
  now: { templateId: string; actingUserId: string },
): 'stay' | 'campaigns-list' {
  if (lastSeen == null || lastSeen.templateId !== now.templateId || lastSeen.actingUserId === now.actingUserId) {
    return 'stay';
  }
  return lastSeen.ownerUserId === now.actingUserId ? 'stay' : 'campaigns-list';
}
